import React, { useRef } from 'react';
import { Group, Line } from 'react-konva';
import Grabber from '../../grabber';
import type { KonvaEventObject } from 'konva/lib/Node';
import type { CylinderType } from 'src/types/transforms';
import { useDragGesture } from 'src/hooks/useDragGesture';

interface EllipseGrabbersProps {
    scaledCylinderWarp: Omit<CylinderType, 'id' | 'fullWrap'>;
    cylinderWarp: CylinderType;
    scale: number;
    onDragMove: (cylinderWarp: CylinderType) => void;
    onDragEnd: () => void;
    cursorChange: (cursor: string) => void;
}

const EllipseGrabbers: React.FC<EllipseGrabbersProps> = (props) => {
    const { scaledCylinderWarp, cylinderWarp, scale, onDragMove: onDragMoveProp, onDragEnd, cursorChange } = props;

    const startCylinderWarpRef = useRef<CylinderType | null>(null);
    const clientXRef = useRef<number | null>(null);
    const clientYRef = useRef<number | null>(null);
    // Which ellipse (top/bottom) is being dragged -- set once at drag-start, read by the single
    // `handleDragMove` for the duration of that gesture.
    const topRef = useRef(false);

    const handleDragMove = (e: MouseEvent) => {
        const deltaX = (clientXRef.current! - e.clientX) / scale;
        const deltaY = (clientYRef.current! - e.clientY) / scale;
        const newCylinderWarp = JSON.parse(JSON.stringify(startCylinderWarpRef.current));
        if (topRef.current) {
            newCylinderWarp.cylinderTop.center.x -= deltaX;
            newCylinderWarp.cylinderTop.center.y -= deltaY;
        } else {
            newCylinderWarp.cylinderBottom.center.x -= deltaX;
            newCylinderWarp.cylinderBottom.center.y -= deltaY;
        }

        onDragMoveProp(newCylinderWarp);
    };

    const { start } = useDragGesture({ onDragMove: handleDragMove, onDragEnd });

    const onDragStart = (e: KonvaEventObject<MouseEvent>, top: boolean) => {
        startCylinderWarpRef.current = cylinderWarp;
        clientXRef.current = e.evt.clientX;
        clientYRef.current = e.evt.clientY;
        topRef.current = top;

        start();
    };

    const top = scaledCylinderWarp.cylinderTop.center;
    const bottom = scaledCylinderWarp.cylinderBottom.center;
    const center = {
        x: (top.x + bottom.x) / 2,
        y: (top.y + bottom.y) / 2,
    };
    const dottedLine = [top.x, top.y, center.x, center.y];

    return (
        <Group>
            <Line points={dottedLine} stroke='rgba(255, 255, 255, .3)' dash={[4, 2]} strokeWidth={1} />
            <Grabber position={top} cursorChange={cursorChange} onDragStart={(e) => onDragStart(e, true)} />
            <Grabber position={bottom} cursorChange={cursorChange} onDragStart={(e) => onDragStart(e, false)} />
        </Group>
    );
};

export default EllipseGrabbers;
